import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Box, Typography, Paper, Grid, Button, IconButton, 
    CircularProgress, Alert, Snackbar, Divider, TablePagination, Switch, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { SettingsService } from '../../api/services/settingsService';
import { HotelService } from '../../api/services/hotelService';
import TaxFormModal from '../../components/TaxFormModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { useCurrency, formatCurrency } from '../../utils/currency';
import { isUaeProperty, getTourismDirhamRate, getTourismDirhamTierLabel } from '../../utils/dubaiHospitalityTax';
import { extractTotalCount, standardFilterSelectSx, standardResetButtonSx, standardRefreshButtonSx } from '../../components/common/tableStyles';

export default function TaxesPage() {
    const { currency, currencySymbol } = useCurrency();
    const { tenants } = useSelector((state) => state.auth);
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId;
    const activeTenant = (tenants || []).find(t => 
        String(t.tenantId || t.id || t._id) === String(tenantId) ||
        String(t.id || t._id) === String(authData.hotelId)
    );
    const [hotelProfile, setHotelProfile] = useState(null);

    useEffect(() => {
        const fetchHotelProfile = async () => {
            try {
                const numericTenantId = authData.tenantId || '1';
                const hotelUuid = activeTenant?.id || activeTenant?._id || authData.hotelId;
                if (hotelUuid) {
                    const res = await HotelService.getById(hotelUuid, numericTenantId);
                    const data = res?.data || res;
                    if (data) {
                        setHotelProfile(data);
                    }
                }
            } catch (err) {
                console.error("Failed to load hotel profile in TaxesPage:", err);
            }
        };
        fetchHotelProfile();
    }, [tenantId, activeTenant?.id, authData.hotelId]);

    const effectiveHotel = hotelProfile || activeTenant || authData?.hotel || authData?.tenant || authData?.property || {};
    const hotel = effectiveHotel;
    const isUae = effectiveHotel.currency === 'AED' || 
                  effectiveHotel.country === 'UAE' || 
                  effectiveHotel.city === 'Dubai' || 
                  String(effectiveHotel.country || '').trim().toUpperCase() === 'UAE' || 
                  String(effectiveHotel.city || '').trim().toLowerCase() === 'dubai' || 
                  currency === 'AED' || 
                  isUaeProperty(effectiveHotel);

    const [taxes, setTaxes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Modal States
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTax, setEditingTax] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taxToDelete, setTaxToDelete] = useState(null);

    // Filter States
    const [filterServiceType, setFilterServiceType] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [totalTaxes, setTotalTaxes] = useState(0);

    useEffect(() => {
        fetchTaxes();
    }, [page, rowsPerPage]);

    const fetchTaxes = async () => {
        setLoading(true);
        try {
            const currentAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = currentAuth.tenantId || '1';
            const data = await SettingsService.getTaxes(tenantId, { page, limit: rowsPerPage });
            const taxList = data?.data || data?.taxes || data;
            setTaxes(Array.isArray(taxList) ? taxList : []);
            setTotalTaxes(extractTotalCount(data, taxList));
        } catch (error) {
            console.error('Failed to fetch taxes:', error);
            setNotification({ open: true, message: 'Failed to load tax settings.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (tax = null) => {
        setEditingTax(tax);
        setModalOpen(true);
    };

    const handleSaveTax = async (taxData) => {
        setSaving(true);
        try {
            const currentAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = currentAuth.tenantId || '1';
            
            if (editingTax) {
                await SettingsService.updateTax(editingTax.id, taxData, tenantId);
                setNotification({ open: true, message: isUae ? 'Tax / fee rule updated successfully!' : 'GST tax slab / rule updated successfully!', severity: 'success' });
            } else {
                await SettingsService.createTax(taxData, tenantId);
                setNotification({ open: true, message: isUae ? 'New tax / fee rule created successfully!' : 'New GST tax slab / rule created successfully!', severity: 'success' });
            }
            
            setModalOpen(false);
            setEditingTax(null);
            await fetchTaxes();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to save tax rule', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (hotel?.rating && taxes.length > 0 && isUae) {
            const statutoryRate = getTourismDirhamRate(hotel.rating, hotel.hotelType);
            const tdTax = taxes.find(t => 
                t && (t.serviceType === 'tourism_dirham' || String(t.name || '').toLowerCase().includes('tourism dirham') || t.rateType === 'flat_per_night')
            );
            if (tdTax && tdTax.status === 'Active' && Number(tdTax.rate) !== statutoryRate) {
                const currentAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
                const numericTenantId = currentAuth.tenantId || '1';
                SettingsService.updateTax(tdTax.id, { rate: statutoryRate }, numericTenantId)
                    .then(() => {
                        setTaxes(prev => prev.map(item => item.id === tdTax.id ? { ...item, rate: statutoryRate } : item));
                    })
                    .catch(e => console.warn("Could not auto-sync statutory Tourism Dirham rate:", e));
            }
        }
    }, [hotel?.rating, hotel?.hotelType, taxes, isUae]);

    const handleSeedDefaultSlabs = async () => {
        setSeeding(true);
        try {
            const currentAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = currentAuth.tenantId || '1';
            const defaultTdRate = getTourismDirhamRate(hotel.rating, hotel.hotelType);
            const defaultSlabs = isUae ? [
                { name: 'UAE Federal VAT', serviceType: 'vat', applicableTo: 'All', rate: 5, rateType: 'percentage', cgst: 0, sgst: 0, igst: 0, allow_itc: true, is_exempt: false, status: 'Active' },
                { name: 'Dubai Municipality Fee', serviceType: 'municipality', applicableTo: 'Room', rate: 7, rateType: 'percentage', cgst: 0, sgst: 0, igst: 0, allow_itc: false, is_exempt: false, status: 'Active' },
                { name: 'Service Charge', serviceType: 'service_charge', applicableTo: 'All', rate: 10, rateType: 'percentage', cgst: 0, sgst: 0, igst: 0, allow_itc: false, is_exempt: false, status: 'Active' },
                { name: 'Tourism Dirham', serviceType: 'tourism_dirham', applicableTo: 'Room', rate: defaultTdRate, rateType: 'flat_per_night', cgst: 0, sgst: 0, igst: 0, allow_itc: false, is_exempt: false, status: 'Active' }
            ] : [
                { name: `GST Room Accommodation (Tier 1: ${currencySymbol}0 – ${currencySymbol}1,000)`, serviceType: 'accommodation', applicableTo: 'Room', minThreshold: 0, maxThreshold: 1000, rate: 0, rateType: 'percentage', cgst: 0, sgst: 0, igst: 0, allow_itc: false, is_exempt: true, status: 'Active' },
                { name: `GST Room Accommodation (Tier 2: ${currencySymbol}1,000.01 – ${currencySymbol}7,500)`, serviceType: 'accommodation', applicableTo: 'Room', minThreshold: 1000.01, maxThreshold: 7500, rate: 5, rateType: 'percentage', cgst: 2.5, sgst: 2.5, igst: 5, allow_itc: false, is_exempt: false, status: 'Active' },
                { name: `GST Room Accommodation (Tier 3: ${currencySymbol}7,500.01+)`, serviceType: 'accommodation', applicableTo: 'Room', minThreshold: 7500.01, maxThreshold: null, rate: 18, rateType: 'percentage', cgst: 9, sgst: 9, igst: 18, allow_itc: true, is_exempt: false, status: 'Active' },
                { name: 'Restaurant / F&B GST', serviceType: 'f_and_b', applicableTo: 'F&B', rate: 5, rateType: 'percentage', cgst: 2.5, sgst: 2.5, igst: 5, allow_itc: false, is_exempt: false, status: 'Active' },
                { name: 'Spa Services GST', serviceType: 'spa_service', applicableTo: 'Spa', rate: 18, rateType: 'percentage', cgst: 9, sgst: 9, igst: 18, allow_itc: true, is_exempt: false, status: 'Active' },
                { name: 'Miscellaneous Amenities GST', serviceType: 'miscellaneous', applicableTo: 'All', rate: 18, rateType: 'percentage', cgst: 9, sgst: 9, igst: 18, allow_itc: true, is_exempt: false, status: 'Active' },
                { name: 'Cancellation Penalty GST', serviceType: 'cancellation_penalty', applicableTo: 'All', rate: 18, rateType: 'percentage', cgst: 9, sgst: 9, igst: 18, allow_itc: true, is_exempt: false, status: 'Active' }
            ];
            for (const slab of defaultSlabs) {
                await SettingsService.createTax(slab, tenantId);
            }
            setNotification({ open: true, message: isUae ? 'Default statutory UAE taxes and fees seeded successfully!' : 'Default statutory GST tax slabs seeded successfully!', severity: 'success' });
            fetchTaxes();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to seed default tax rules', severity: 'error' });
        } finally {
            setSeeding(false);
        }
    };

    const handleStatusToggle = async (tax) => {
        try {
            const currentAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = currentAuth.tenantId || '1';
            const newStatus = tax.status === 'Inactive' ? 'Active' : 'Inactive';
            const updatedTax = isUae ? { status: newStatus } : { ...tax, status: newStatus };
            await SettingsService.updateTax(tax.id, updatedTax, tenantId);
            setTaxes(prev => prev.map(t => t.id === tax.id ? { ...t, status: newStatus } : t));
            setNotification({ open: true, message: `Tax rule status updated to ${newStatus}`, severity: 'success' });
            await fetchTaxes();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to update tax status', severity: 'error' });
        }
    };

    const handleDeleteClick = (tax) => {
        setTaxToDelete(tax);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!taxToDelete) return;
        
        setDeleting(true);
        try {
            const currentAuth = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = currentAuth.tenantId || '1';
            await SettingsService.deleteTax(taxToDelete.id, tenantId);
            setTaxes(prev => prev.filter(t => t.id !== taxToDelete.id));
            setNotification({ open: true, message: 'Tax rule deleted successfully!', severity: 'success' });
            setDeleteModalOpen(false);
            await fetchTaxes();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Failed to delete tax rule', severity: 'error' });
        } finally {
            setDeleting(false);
            setTaxToDelete(null);
        }
    };

    const formatFeeName = (tax) => {
        const s = String(tax.serviceType || tax.applicableTo || '').toLowerCase();
        const n = String(tax.name || '').trim().toLowerCase();
        if (n === 'vat' || s === 'vat' || n.includes('uae vat') || n.includes('federal vat')) return 'UAE Federal VAT';
        if (n === 'municipality' || s === 'municipality' || n.includes('municipality')) return 'Dubai Municipality Fee';
        if (n === 'service_charge' || n === 'service charge' || s === 'service_charge' || n.includes('service charge')) return 'Service Charge';
        if (n === 'tourism_dirham' || n === 'tourism dirham' || s === 'tourism_dirham' || tax.rateType === 'flat_per_night' || n.includes('tourism dirham')) return 'Tourism Dirham';
        if (tax.name && !['statutory slab', 'tax slab', 'vat', 'municipality', 'service_charge', 'tourism_dirham'].includes(n)) {
            return tax.name;
        }
        if (s === 'room') return 'Room Accommodation';
        if (s === 'f_and_b') return 'Food & Beverage';
        if (s === 'spa_service') return 'Spa Services';
        return tax.name || 'Statutory Levy / Fee';
    };

    const getRawKey = (tax) => {
        const s = String(tax.serviceType || '').toLowerCase();
        const n = String(tax.name || '').toLowerCase();
        if (s === 'vat' || n.includes('vat')) return 'vat';
        if (s === 'municipality' || n.includes('municipality')) return 'municipality';
        if (s === 'service_charge' || n.includes('service charge')) return 'service_charge';
        if (s === 'tourism_dirham' || n.includes('tourism dirham') || tax.rateType === 'flat_per_night') return 'tourism_dirham';
        return tax.serviceType || tax.applicableTo || 'room';
    };

    const formatScopeRule = (tax) => {
        const s = String(tax.serviceType || tax.applicableTo || '').toLowerCase();
        const n = String(tax.name || '').toLowerCase();
        if (s === 'vat' || n.includes('vat')) {
            return 'Applied to Room + Service Charge + Municipality';
        }
        if (s === 'municipality' || n.includes('municipality')) {
            return 'Applied to Room + Service Charge';
        }
        if (s === 'service_charge' || n.includes('service charge')) {
            return 'Property service charge on room tariff';
        }
        if (s === 'tourism_dirham' || n.includes('tourism dirham') || tax.rateType === 'flat_per_night') {
            const tierLabel = getTourismDirhamTierLabel(hotel.rating, hotel.hotelType);
            return `Max 30 consecutive nights (${tierLabel})`;
        }
        if (s === 'f_and_b' || s === 'restaurant') {
            return 'Applied to Food & Beverage charges';
        }
        if (s === 'spa_service' || s === 'spa') {
            return 'Applied to Spa & Wellness services';
        }
        return tax.applicableTo || 'Applied to standard room tariff';
    };

    const formatApplicableScope = (tax) => {
        const s = String(tax.applicableTo || tax.serviceType || '').toLowerCase();
        const n = String(tax.name || '').toLowerCase();
        if (s === 'tourism_dirham' || n.includes('tourism dirham') || tax.rateType === 'flat_per_night') {
            return 'Per Bedroom / Night';
        }
        if (s === 'all' || s === 'all services' || s === 'vat' || s === 'service_charge' || n.includes('vat') || n.includes('service charge')) {
            return 'All Services';
        }
        if (s === 'room' || s === 'accommodation' || s === 'municipality' || n.includes('municipality')) {
            return 'Room Accommodation';
        }
        if (s === 'f_and_b' || s === 'restaurant') return 'Food & Beverage';
        if (s === 'spa_service' || s === 'spa') return 'Spa Services';
        if (s === 'miscellaneous') return 'Miscellaneous Amenities';
        if (s === 'cancellation_penalty') return 'Cancellation Penalties';
        return tax.applicableTo || 'All Services';
    };

    const formatAppliedTo = (tax) => {
        const cat = tax.serviceType || tax.applicableTo || tax.category;
        const nameLower = String(tax.name || '').toLowerCase();
        if (isUae) {
            if (cat === 'vat' || nameLower.includes('vat')) return 'UAE Federal VAT';
            if (cat === 'municipality' || nameLower.includes('municipality')) return 'Dubai Municipality Fee';
            if (cat === 'service_charge' || nameLower.includes('service charge')) return 'Service Charge';
            if (cat === 'tourism_dirham' || tax.rateType === 'flat_per_night' || nameLower.includes('tourism dirham')) {
                return 'Tourism Dirham';
            }
        }
        if (cat === 'f_and_b' || cat === 'F&B' || cat === 'Restaurant') return 'Food & Beverage (Standard)';
        if (cat === 'f_and_b_specified') return 'Food & Beverage (Specified Premises)';
        if (cat === 'liquor_vat') return 'Liquor VAT / Alcohol';
        if (cat === 'spa_service' || cat === 'Spa') return 'Spa Services';
        if (cat === 'miscellaneous') return 'Miscellaneous Amenities';
        if (cat === 'cancellation_penalty') return 'Cancellation Penalties';
        if (cat === 'accommodation' || cat === 'room' || cat === 'Room' || cat === 'All' || !cat) return 'Room Accommodation';
        return tax.name || cat;
    };

    const handleResetFilters = () => {
        setFilterServiceType('All');
        setFilterStatus('All');
        setPage(0);
    };

    const filteredTaxes = taxes.filter(tax => {
        let match = true;
        if (filterServiceType !== 'All') {
            const cat = tax.serviceType || tax.applicableTo || tax.category;
            let normalizedCat = cat;
            if (isUae) {
                if (cat === 'tourism_dirham' || tax.rateType === 'flat_per_night' || String(tax.name || '').toLowerCase().includes('tourism dirham')) {
                    normalizedCat = 'tourism_dirham';
                } else if (cat === 'vat') normalizedCat = 'vat';
                else if (cat === 'municipality') normalizedCat = 'municipality';
                else if (cat === 'service_charge') normalizedCat = 'service_charge';
            }
            if (normalizedCat !== 'tourism_dirham' && normalizedCat !== 'vat' && normalizedCat !== 'municipality' && normalizedCat !== 'service_charge') {
                if (cat === 'accommodation' || cat === 'Room' || cat === 'All' || !cat) normalizedCat = 'room';
                if (cat === 'F&B' || cat === 'Restaurant') normalizedCat = 'f_and_b';
                if (cat === 'Spa') normalizedCat = 'spa_service';
            }
            
            if (normalizedCat !== filterServiceType) match = false;
        }
        if (filterStatus !== 'All') {
            const isInactive = tax.status === 'Inactive';
            if (filterStatus === 'Active' && isInactive) match = false;
            if (filterStatus === 'Inactive' && !isInactive) match = false;
        }
        return match;
    });


    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                    {isUae ? 'VAT, Municipality & Hospitality Fees' : 'GST Tax Slabs & Fees'}
                </Typography>
            </Box>

            <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', p: 3 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Statutory Configuration</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <TextField
                                    select
                                    size="small"
                                    value={filterServiceType}
                                    onChange={(e) => { setFilterServiceType(e.target.value); setPage(0); }}
                                    SelectProps={{ displayEmpty: true }}
                                    sx={{ minWidth: 200, ...standardFilterSelectSx }}
                                >
                                    <MenuItem value="All">All Services</MenuItem>
                                    {isUae ? [
                                        <MenuItem key="vat" value="vat">UAE Federal VAT</MenuItem>,
                                        <MenuItem key="municipality" value="municipality">Dubai Municipality Fee</MenuItem>,
                                        <MenuItem key="service_charge" value="service_charge">Service Charge</MenuItem>,
                                        <MenuItem key="tourism_dirham" value="tourism_dirham">Tourism Dirham</MenuItem>,
                                        <MenuItem key="room" value="room">Room Accommodation</MenuItem>,
                                        <MenuItem key="f_and_b" value="f_and_b">Food & Beverage</MenuItem>,
                                        <MenuItem key="spa_service" value="spa_service">Spa Services</MenuItem>,
                                        <MenuItem key="miscellaneous" value="miscellaneous">Miscellaneous Amenities</MenuItem>,
                                        <MenuItem key="cancellation_penalty" value="cancellation_penalty">Cancellation Penalties</MenuItem>
                                    ] : [
                                        <MenuItem key="room" value="room">Room Accommodation</MenuItem>,
                                        <MenuItem key="f_and_b" value="f_and_b">Food & Beverage (Standard)</MenuItem>,
                                        <MenuItem key="f_and_b_specified" value="f_and_b_specified">Food & Beverage (Specified Premises)</MenuItem>,
                                        <MenuItem key="liquor_vat" value="liquor_vat">Liquor VAT / Alcohol</MenuItem>,
                                        <MenuItem key="spa_service" value="spa_service">Spa Services</MenuItem>,
                                        <MenuItem key="miscellaneous" value="miscellaneous">Miscellaneous Amenities</MenuItem>,
                                        <MenuItem key="cancellation_penalty" value="cancellation_penalty">Cancellation Penalties</MenuItem>
                                    ]}
                                </TextField>
                                <TextField
                                    select
                                    size="small"
                                    value={filterStatus}
                                    onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
                                    SelectProps={{ displayEmpty: true }}
                                    sx={{ minWidth: 130, ...standardFilterSelectSx }}
                                >
                                    <MenuItem value="All">All Statuses</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </TextField>
                                {(filterServiceType !== 'All' || filterStatus !== 'All') && (
                                    <Button 
                                        variant="outlined" 
                                        onClick={handleResetFilters}
                                        startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                                        sx={standardResetButtonSx}
                                    >
                                        Reset
                                    </Button>
                                )}
                                <IconButton onClick={fetchTaxes} disabled={loading} size="small" sx={standardRefreshButtonSx}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                        
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    {isUae ? (
                                        <TableRow>
                                            <TableCell>Tax / Fee Name</TableCell>
                                            <TableCell>Fee Type</TableCell>
                                            <TableCell>Rate / Amount</TableCell>
                                            <TableCell>Scope / Rule</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>Status</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>Action</TableCell>
                                        </TableRow>
                                    ) : (
                                        <TableRow>
                                            <TableCell>Service Type</TableCell>
                                            <TableCell>Tariff Range ({currencySymbol})</TableCell>
                                            <TableCell>Total GST %</TableCell>
                                            <TableCell>CGST / SGST Breakdown (50:50)</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>ITC Allowed</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>Exempt</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>Status</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>Action</TableCell>
                                        </TableRow>
                                    )}
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={isUae ? 6 : 8} align="center" sx={{ py: 4 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <CircularProgress size={24} sx={{ mr: 2 }} />
                                                    <Typography color="text.secondary">{isUae ? 'Loading statutory taxes & fees...' : 'Loading statutory GST slabs...'}</Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredTaxes.length > 0 ? (
                                        filteredTaxes.map((tax, idx) => {
                                            const isInactive = tax.status === 'Inactive';
                                            const isFlat = tax.rateType === 'flat_per_night' || 
                                                tax.serviceType === 'tourism_dirham' || 
                                                tax.type === 'tourism_dirham' ||
                                                String(tax.name || '').toLowerCase().includes('tourism dirham');
                                            const isVat = tax.serviceType === 'vat' || 
                                                tax.type === 'vat' || 
                                                String(tax.name || '').toLowerCase().includes('vat');
                                            
                                            // Primary fields: rate and rateType
                                            const primaryRate = tax.rate !== undefined && tax.rate !== null && tax.rate !== '' 
                                                ? Number(tax.rate) 
                                                : (tax.gstPct !== undefined && tax.gstPct !== null && tax.gstPct !== '' ? Number(tax.gstPct) : (Number(tax.cgstPct || tax.cgst || 0) + Number(tax.sgstPct || tax.sgst || 0)));

                                            if (isUae) {
                                                return (
                                                    <TableRow 
                                                        key={tax.id || idx}
                                                        hover
                                                        sx={{ 
                                                            bgcolor: isInactive ? 'grey.50' : 'inherit',
                                                            opacity: isInactive ? 0.7 : 1,
                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                        }}
                                                    >
                                                        {/* 1. Tax / Fee Name */}
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {formatFeeName(tax)}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
                                                                {getRawKey(tax)}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* 2. Fee Type */}
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.primary">
                                                                {isFlat ? 'Flat Fee' : 'Percentage'}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* 3. Rate / Amount */}
                                                        <TableCell>
                                                            {isFlat ? (
                                                                <Chip 
                                                                    label={`AED ${Number(primaryRate).toFixed(2)} / room / night`}
                                                                    size="small"
                                                                    sx={{ 
                                                                        fontWeight: 600, 
                                                                        bgcolor: '#fef3c7', 
                                                                        color: '#92400e',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid #fde68a'
                                                                    }} 
                                                                />
                                                            ) : isVat ? (
                                                                <Chip 
                                                                    label={`${primaryRate}% VAT`}
                                                                    size="small"
                                                                    sx={{ 
                                                                        fontWeight: 600, 
                                                                        bgcolor: '#e0e7ff', 
                                                                        color: '#3730a3',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid #c7d2fe'
                                                                    }} 
                                                                />
                                                            ) : (
                                                                <Chip 
                                                                    label={`${primaryRate}%`}
                                                                    size="small"
                                                                    sx={{ 
                                                                        fontWeight: 600, 
                                                                        bgcolor: '#e0e7ff', 
                                                                        color: '#3730a3',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid #c7d2fe'
                                                                    }} 
                                                                />
                                                            )}
                                                        </TableCell>

                                                        {/* 4. Scope / Rule */}
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatScopeRule(tax)}
                                                            </Typography>
                                                        </TableCell>

                                                        {/* 5. Status */}
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                                <Typography variant="body2" color={isInactive ? 'text.secondary' : 'text.primary'}>
                                                                    {isInactive ? 'Inactive' : 'Active'}
                                                                </Typography>
                                                                <Switch 
                                                                    size="small" 
                                                                    checked={!isInactive} 
                                                                    onChange={() => handleStatusToggle(tax)} 
                                                                    color="primary"
                                                                />
                                                            </Box>
                                                        </TableCell>

                                                        {/* 6. Action */}
                                                        <TableCell align="center">
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleOpenModal(tax)} 
                                                                sx={{ color: '#d97706', bgcolor: 'rgba(217, 119, 6, 0.08)', '&:hover': { bgcolor: 'rgba(217, 119, 6, 0.16)' } }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }

                                            // Indian GST Layout (!isUae)
                                            const hasThreshold = (tax.minThreshold !== undefined && tax.minThreshold !== null && tax.minThreshold !== '') || (tax.maxThreshold !== undefined && tax.maxThreshold !== null && tax.maxThreshold !== '');
                                            const isExempt = Boolean(tax.isExempt || tax.is_exempt);

                                            return (
                                                <TableRow 
                                                    key={tax.id || idx}
                                                    hover
                                                    sx={{ 
                                                        bgcolor: isInactive ? 'grey.50' : 'inherit',
                                                        opacity: isInactive ? 0.7 : 1,
                                                        '&:last-child td, &:last-child th': { border: 0 }
                                                    }}
                                                >
                                                    {/* 1. Service Type */}
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {formatAppliedTo(tax)}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* 2. Tariff Range (₹) */}
                                                    <TableCell>
                                                        {hasThreshold ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatCurrency(tax.minThreshold || 0, currency)} – {tax.maxThreshold ? formatCurrency(tax.maxThreshold, currency) : 'Above'}
                                                            </Typography>
                                                        ) : <Typography variant="body2" color="text.secondary">Any / Flat</Typography>}
                                                    </TableCell>

                                                    {/* 3. Total GST % */}
                                                    <TableCell>
                                                        <Chip 
                                                            label={`${primaryRate}% GST`}
                                                            size="small"
                                                            sx={{ 
                                                                fontWeight: 'bold', 
                                                                bgcolor: isExempt ? 'grey.200' : 'primary.main', 
                                                                color: isExempt ? 'text.primary' : '#fff' 
                                                            }} 
                                                        />
                                                    </TableCell>

                                                    {/* 4. CGST / SGST Breakdown (50:50) */}
                                                    <TableCell>
                                                        {isExempt ? (
                                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                                        ) : (
                                                            <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                                                {tax.cgstPct !== undefined ? tax.cgstPct : (tax.cgst !== undefined ? tax.cgst : (primaryRate / 2))}% CGST <br/> 
                                                                {tax.sgstPct !== undefined ? tax.sgstPct : (tax.sgst !== undefined ? tax.sgst : (primaryRate / 2))}% SGST
                                                            </Typography>
                                                        )}
                                                    </TableCell>

                                                    {/* 5. ITC Allowed */}
                                                    <TableCell align="center">
                                                        {(tax.allowItc || tax.allow_itc) ? (
                                                            <Chip label="Yes" size="small" color="success" variant="outlined" sx={{ height: 22, fontWeight: 'bold' }} />
                                                        ) : (
                                                            <Chip label="No" size="small" variant="outlined" sx={{ height: 22, color: 'text.secondary' }} />
                                                        )}
                                                    </TableCell>

                                                    {/* 6. Exempt */}
                                                    <TableCell align="center">
                                                        {isExempt ? (
                                                            <Chip label="Exempt" size="small" color="warning" sx={{ height: 22, fontWeight: 'bold' }} />
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">No</Typography>
                                                        )}
                                                    </TableCell>

                                                    {/* 7. Status */}
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                            <Switch 
                                                                size="small" 
                                                                checked={!isInactive} 
                                                                onChange={() => handleStatusToggle(tax)} 
                                                                color="primary"
                                                            />
                                                            <Typography variant="caption" color={isInactive ? 'text.secondary' : 'success.main'} fontWeight="600">
                                                                {isInactive ? 'Inactive' : 'Active'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    {/* 8. Action */}
                                                    <TableCell align="center">
                                                        <IconButton size="small" onClick={() => handleOpenModal(tax)} color="primary" sx={{ bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={isUae ? 6 : 8} align="center" sx={{ py: 6 }}>
                                                <Typography color="text.secondary">
                                                    {isUae ? 'No statutory taxes or hospitality fees configured' : 'No GST tax slabs or rules configured'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={taxes.length === 0 ? 0 : totalTaxes}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[20, 50, 100]}
                            />
                        </TableContainer>
                    </Grid>
                    

                </Grid>
            </Paper>

            <TaxFormModal 
                open={modalOpen}
                onClose={() => { setModalOpen(false); setEditingTax(null); }}
                onSave={handleSaveTax}
                editingTax={editingTax}
                loading={saving}
                hotel={hotel}
            />

            <DeleteConfirmationModal 
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Tax Rule"
                message={taxToDelete ? `Are you sure you want to permanently delete the "${taxToDelete.name}" tax rule?` : ''}
                loading={deleting}
            />

            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
